import { ShapeToolSubMenu } from "@/appInterface/windows/shapeToolSubMenu";
import { canv } from "@/canv/global";
import { lightOrDark } from "@/canv/utils";
import { getViewport } from "@/canvasController/helpers";
import { ShapeWidget } from "@/elements/shape";
import { Group } from "@/group";
import { t } from "@/i18n";
import { toolBase } from "@/tools/toolBase";
import { isMobile } from "@/utils";
import { selectionManager, transformManager } from "../cursorTool";

export class ShapeTool extends toolBase {
    constructor() {
        super();

        this.basicShapes = [
            "rect",
            "roundedRect",
            "circle",
            "triangle",
            "downTriangle",
            "rombus",
            "RSkewRect",
            "LSkewRect",
            "bubble",
            "arrowRight",
            "arrowLeft",
            "arrowLeftRight",
            "pentagon",
            "octagon",
            "hexagon",
            "trapezoid",
            "predefinedProcess",
            "cross",
            "braceLeft",
            "braceRight",
            "star",
            "cloud",
        ];
        this.diagramShapes = [
            "process",
            "terminator",
            "decision",
            "document",
            "linedDocument",
            "multipleDocuments",
            "data",
            "merge",
            "offPageConnector",
            "preparation",
            "manualOperation",
            "manualInput",
            "linedShaded",
            "divided",
            "internalStorage",
            "horizontalSwimlane",
            "predefinedProcessFlowchart",
            "note",
            "circle",
            "or",
            "summingJunction",
            "delay",
            "database",
            "directAccessStorage",
            "storedData",
            "display",
            "note2",
            "verticalSwimlane",
            "curlyBraceAnnotationLeft",
            "curlyBraceAnnotationRight",
            "men",
        ];
        this.bpmnShapes = [
            "task",
            "transaction",
            "eventSubProcess",
            "callActivity",
            "group",
            "pool",
            "startEvent",
            "startEventNonInterrupting",
            "intermediateEvent",
            "intermediateEventNonInterrupting",
            "endEvent",
            "conversation",
            "gateWay",
            "dataObject",
            "dataStore",
            "horizontalSwimlaneBpmn",
            "verticalSwimlaneBpmn",
            "annotation",
            "message",
        ];
        this.umlShapes = [
            "activity",
            "interface",
            "template",
            "container",
            "containerDivided",
            "classifier",
            "sendSignal",
            "offPageLink",
            "gateWayUml",
            "receiveSignal",
            "package",
            "node",
            "useCase",
            "collaboration",
            "start",
            "end",
            "flowFinal",
            "historyPseudoState",
            "pin",
            "pinRight",
            "pinFilledRight",
            "pinLeft",
            "pinFilledLeft",
            "deletion",
            "horizontalFork",
            "verticalFork",
            "activation",
            "horizontalPool",
            "verticalPool",
            "person",
            "assembly",
            "providedInterface",
            "requiredInterface",
        ];
        this.subMenuShapes = [
            ...new Set([
                ...this.basicShapes,
                ...this.diagramShapes,
                ...this.bpmnShapes,
                ...this.umlShapes,
            ]),
        ];
        this.shapeToolSubMenu = new ShapeToolSubMenu();
    }

    getToolView() {
        return `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.75 12H2.25V20.25C2.25 20.6478 2.40804 21.0294 2.68934 21.3107C2.97064 21.592 3.35218 21.75 3.75 21.75H9.75V20.25H3.75V12ZM21 21.75H12.75C12.6197 21.75 12.4916 21.716 12.3784 21.6514C12.2652 21.5868 12.1708 21.4938 12.1045 21.3816C12.0382 21.2694 12.0022 21.1419 12.0002 21.0116C11.9982 20.8813 12.0302 20.7527 12.093 20.6385L16.218 13.1385C16.2821 13.0203 16.377 12.9218 16.4928 12.8534C16.6085 12.7849 16.7406 12.7492 16.875 12.75C17.1322 12.75 17.3895 12.879 17.532 13.1385L21.657 20.6385C21.7198 20.7527 21.7518 20.8813 21.7498 21.0116C21.7478 21.1419 21.7118 21.2694 21.6455 21.3816C21.5792 21.4938 21.4848 21.5868 21.3716 21.6514C21.2584 21.716 21.1303 21.75 21 21.75ZM14.0182 20.25H19.7318L16.875 15.0563L14.0182 20.25ZM20.25 2.25H12V3.75H20.25V14.244H21.75V3.75C21.75 3.35218 21.592 2.97064 21.3107 2.68934C21.0294 2.40804 20.6478 2.25 20.25 2.25ZM8.25 2.25H3.75C3.35218 2.25 2.97064 2.40804 2.68934 2.68934C2.40804 2.97064 2.25 3.35218 2.25 3.75V8.25C2.25 8.64782 2.40804 9.02936 2.68934 9.31066C2.97064 9.59196 3.35218 9.75 3.75 9.75H8.25C8.64782 9.75 9.02936 9.59196 9.31066 9.31066C9.59196 9.02936 9.75 8.64782 9.75 8.25V3.75C9.75 3.35218 9.59196 2.97064 9.31066 2.68934C9.02936 2.40804 8.64782 2.25 8.25 2.25ZM8.25 8.25H3.75V3.75H8.25V8.25Z" fill="currentColor"/>
            </svg>
        `;
    }

    getToolTipName() {
        return t("tools.shapes");
    }

    getToolHotKey() {
        return "S";
    }

    select() {
        super.select();
        this.openSubMenu();
    }

    unselect() {
        super.unselect();
        this.closeSubMenu();
        this.ghost = null;
        this.clickMove = 0;
        this.mode = null;
    }

    openSubMenu() {
        this.shapeToolSubMenu.open(
            null,
            this.setMode.bind(this),
            this.basicShapes,
            this.diagramShapes,
            this.bpmnShapes,
            this.umlShapes,
        );
    }

    closeSubMenu() {
        this.shapeToolSubMenu.close();
    }

    openShapeLibrary() {
        this.closeSubMenu();
    }

    setMode(mode: string, isOnlyEditShapeMode = false) {
        this.mode = mode;

        if (canv.shapeStorage.needStroke(mode) && this.borderColor === "transparent") {
            this.borderColor = "black";
        }

        if (canv.shapeStorage.disableFill(mode)) {
            this.color = "transparent";
        }

        this.borderDashType = canv.shapeStorage.getDefaultBorderDashType(mode);
        this.borderWidth = canv.shapeStorage.getDefaultBorderWidth(mode);

        if (this.color === "transparent") {
            this.color = canv.shapeStorage.getDefaultColor(mode);
        }

        if (isOnlyEditShapeMode) return;

        if (isMobile()) {
            this.createShapeAndSwitchToCursor();
        } else {
            this.updateShapeToolSubMenu();
        }
    }

    createShapeAndSwitchToCursor() {
        const newShapeWrapper = this.getWidgetWrapper();

        const viewport = getViewport();
        const positionX =
            viewport.viewportStartX +
            viewport.viewportWidth / 2 -
            newShapeWrapper.startX -
            newShapeWrapper.width / 2;
        const positionY =
            viewport.viewportStartY +
            viewport.viewportHeight / 2 -
            newShapeWrapper.startY -
            newShapeWrapper.height / 2;
        newShapeWrapper.translateGroup(positionX, positionY);

        canv.canvasController.addGroup(newShapeWrapper);
        canv.eventManager.openRecord();
        canv.eventManager.createBuildEvent("create", newShapeWrapper);
        canv.eventManager.closeRecord();
        canv.toolBox.switchToByName("cursorTool");
    }

    updateShapeToolSubMenu() {
        // Обновляем React компонент с новым выбранным режимом
        if (this.shapeToolSubMenu.root) {
            this.shapeToolSubMenu.update(
                this.mode,
                this.setMode.bind(this),
                this.basicShapes,
                this.diagramShapes,
                this.bpmnShapes,
                this.umlShapes,
            );
        }

        this.resetGhost();
        canv.redraw();
    }

    setColor(color: string) {
        this.color = color;
        this.resetGhost();
    }

    setBorderColor(color: string) {
        this.borderColor = color;
        this.resetGhost();
    }

    setBorderDashType(borderDashType: string) {
        this.borderDashType = borderDashType;
        this.resetGhost();
    }

    setBorderWidth(borderWidth: number) {
        this.borderWidth = borderWidth;
        this.resetGhost();
    }

    getWidgetWrapper(isGhost = false) {
        const shape = canv.shapeStorage.getShape(this.mode || undefined);

        const width = shape.width || 200;
        const height = shape.height || 200;

        const newShape = new ShapeWidget(0, 0, width, height);

        newShape.mode = this.mode as string;
        newShape.color = this.color;

        if (lightOrDark(this.color) === "light") {
            newShape.textPart.menuTextColor("black", true);
        } else {
            newShape.textPart.menuTextColor("white", true);
        }

        newShape.borderColor = this.borderColor;
        newShape.borderDashType = this.borderDashType;
        newShape.borderWidth = this.borderWidth;

        const newGroup = new Group(true, isGhost);
        newShape.setParent(newGroup);
        return newGroup;
    }

    createShape() {
        const pt = canv.actionManager.getActionPoint();

        const creationStartX = pt.x;
        const creationStartY = pt.y;

        const newGroup = this.getWidgetWrapper();
        canv.canvasController.addGroup(newGroup);

        newGroup.translateGroup(creationStartX - newGroup.startX, creationStartY - newGroup.startY);

        canv.eventManager.openRecord();
        canv.eventManager.createBuildEvent("create", newGroup);
        canv.eventManager.closeRecord();

        canv.toolBox.TryGetToolByName("cursorTool").setSelectedAndInteraction(newGroup);
    }

    resetGhost() {
        this.ghost = null;
    }

    TryAction(type: string) {
        // Не создаем ghost, если фигура не выбрана
        if (!this.mode) {
            return;
        }

        // если сразу сделать mouseup не дождавшись mousemove,
        // ghost тоже должен быть, т.к. появились хоткеи для фигур и
        // пользователь может не начать двигать курсор, а начать сразу создавать фигуру
        if (this.ghost == null) {
            this.ghost = this.getWidgetWrapper(true);
        }

        if (type === "mousedown") {
            this.clickMove = 1;

            const pt = canv.actionManager.getActionPoint();

            this.creationStartX = pt.x;
            this.creationStartY = pt.y;
        } else if (type === "mousemove") {
            if (this.ghost == null) {
                this.ghost = this.getWidgetWrapper(true);
            }

            if (this.clickMove > 0) {
                this.clickMove++;
            }

            if (this.clickMove === 0) {
                const pt = canv.actionManager.getActionPoint();

                const translateX = pt.x - this.ghost.startX;
                const translateY = pt.y - this.ghost.startY;

                this.ghost.translateGroup(translateX, translateY);
            } else if (this.clickMove > 2) {
                if (this.ghost == null) return;

                // Уменьшаем shape на 199, чтобы получилось width = 1, height = 1
                this.ghost.resizeGroup(-199, -199, "");

                canv.canvasController.addGroup(this.ghost);
                canv.eventManager.openRecord();
                canv.eventManager.createBuildEvent("create", this.ghost);
                canv.eventManager.closeRecord();

                const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");

                selectionManager.setSelected([this.ghost], false);
                cursorTool.turnMode("resize");
                transformManager.setCatchPointer("bottomRight");
            }
        } else if (type === "mouseup") {
            if (this.clickMove > 0 || this.clickMove <= 2) {
                if (this.ghost == null) return;

                canv.canvasController.addGroup(this.ghost);
                canv.eventManager.openRecord();
                canv.eventManager.createBuildEvent("create", this.ghost);
                canv.eventManager.closeRecord();
                canv.toolBox.TryGetToolByName("cursorTool").setSelectedAndInteraction(this.ghost);
            }
        }

        canv.redraw();
    }

    draw() {
        if (this.ghost == null) return;

        canv.ctx.globalAlpha = 0.2;
        this.ghost.draw();
        canv.ctx.globalAlpha = 1;
    }

    name = "shapeTool";
    ghost: Group | null = null;

    clickMove = 0;
    creationStartX = 0;
    creationStartY = 0;
    shapeToolSubMenu: ShapeToolSubMenu;
    basicShapes: string[];
    diagramShapes: string[];
    bpmnShapes: string[];
    umlShapes: string[];
    subMenuShapes: string[];

    mode: string | null = null;
    color = "transparent"; //"#0c8de9";//"#3D62E1";
    borderColor = "black"; //"transparent";
    borderDashType = "solid";
    borderWidth = 1;
}
